import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import './LeaderboardView.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function LeaderboardView({ factFictionMode }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [scope, setScope] = useState('club');
  const [entries, setEntries] = useState([]);
  const [userPosition, setUserPosition] = useState(null);
  const [percentile, setPercentile] = useState(null);
  const [optedIn, setOptedIn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [optingIn, setOptingIn] = useState(false);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(instance, `/api/gamification/leaderboard?scope=${scope}`);
      if (res.status === 403) {
        setOptedIn(false);
        return;
      }
      if (!res.ok) throw new Error('Failed to load leaderboard');
      const data = await res.json();
      if (data?.optedIn === false) {
        setOptedIn(false);
        setEntries([]);
        return;
      }
      setOptedIn(true);
      setEntries(unwrap(data?.entries || data));
      setUserPosition(data?.userPosition ?? null);
      setPercentile(data?.percentile ?? null);
    } catch (err) {
      if (err.message?.includes('opt')) {
        setOptedIn(false);
      } else {
        setError(err.message || 'Failed to load leaderboard');
      }
    } finally {
      setLoading(false);
    }
  }, [instance, scope]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const handleOptIn = async () => {
    setOptingIn(true);
    try {
      await apiFetchJson(instance, '/api/gamification/leaderboard/opt-in', { method: 'POST' });
      setOptedIn(true);
      fetchLeaderboard();
    } catch (err) {
      setError(err.message || 'Failed to opt in');
    } finally {
      setOptingIn(false);
    }
  };

  const seasonLabel = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  if (loading) return <div className="leaderboard-loading">{t('bookClub.leaderboard.loading')}</div>;
  if (error) return <div className="leaderboard-error">{error} <button onClick={fetchLeaderboard}>{t('bookClub.common.retry')}</button></div>;

  if (!optedIn) {
    return (
      <div className="leaderboard-container">
        <div className="leaderboard-optin-card">
          <h3 className="leaderboard-optin-title">{t('bookClub.leaderboard.joinTitle')}</h3>
          <p className="leaderboard-optin-desc">
            {t('bookClub.leaderboard.joinDesc')}
          </p>
          <button
            className="leaderboard-optin-btn"
            onClick={handleOptIn}
            disabled={optingIn}
          >
            {optingIn ? t('bookClub.leaderboard.joining') : t('bookClub.leaderboard.joinBtn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <span className="leaderboard-season">{seasonLabel}</span>
      </div>

      {/* Scope tabs */}
      <div className="leaderboard-tabs">
        {['club', 'household', 'global'].map(s => (
          <button
            key={s}
            className={`leaderboard-tab ${scope === s ? 'active' : ''}`}
            onClick={() => setScope(s)}
          >
            {t(`bookClub.leaderboard.${s}`)}
          </button>
        ))}
      </div>

      {/* User position */}
      {userPosition != null && (
        <div className="leaderboard-user-position">
          {t('bookClub.leaderboard.yourRank')} <strong>#{userPosition}</strong>
          {percentile != null && (
            <span className="leaderboard-percentile"> — {t('bookClub.leaderboard.top')} {percentile}%</span>
          )}
        </div>
      )}

      {/* Entries list */}
      <div className="leaderboard-list">
        {entries.length === 0 ? (
          <p className="leaderboard-empty">{t('bookClub.leaderboard.empty')}</p>
        ) : (
          entries.map((entry, idx) => {
            const rank = entry.rank ?? idx + 1;
            const isUser = entry.isCurrentUser;
            const medalClass = rank <= 3 ? `medal-${rank}` : '';
            return (
              <div
                key={entry.userId || idx}
                className={`leaderboard-row ${medalClass} ${isUser ? 'current-user' : ''}`}
              >
                <span className="leaderboard-rank">{rank}</span>
                <span className="leaderboard-name">{entry.displayName || entry.name || 'Reader'}</span>
                <span className="leaderboard-xp">{entry.xp ?? 0} {t('bookClub.streaks.xp')}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default LeaderboardView;
